/**
 * `useFullWidth()` — the demo's "⤢ Full width" mode (demo lines 1955–1958,
 * key binding at 2007).
 *
 * Full width gives the work area the whole window: the menu rail and the
 * assistant collapse together, and turning it off puts back exactly what was
 * open before — a rail the presenter had already collapsed stays collapsed.
 * The How-To Guide recommends it for projectors.
 *
 * It owns no layout of its own. The two panels already have state — the rail
 * in `SidebarStateContext`, the assistant in `ChatContext` (which drives the
 * resizable panel imperatively) — and this hook only flips both and remembers
 * what they were.
 *
 * - **F** toggles it from anywhere, as in the demo: ignored while typing in an
 *   input, select, textarea or editable region, inside a dialog, with any
 *   modifier held, or on key repeat.
 * - Reopening either panel by hand while full width is on ends the mode
 *   without restoring anything, so the button never claims a state the
 *   window is no longer in.
 *
 * Engine layer: it orchestrates state and renders nothing. Mount it **once** —
 * `OemShell` does, and hands the result to the top bar — or the F key would
 * toggle twice.
 *
 * @see src/versions/v1/chrome-oem/OemShell.tsx
 * @see docs/logic/workspace/search-and-view.md
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useChat } from './ChatContext';
import { useSidebarState } from './SidebarStateContext';

export interface FullWidthState {
  fullWidth: boolean;
  toggleFullWidth: () => void;
}

/** What was open before full width went on. */
interface PanelSnapshot {
  readonly sidebar: boolean;
  readonly rail: boolean;
}

/** True when a keystroke belongs to a text field or a dialog, not the page. */
function typingIn(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (/^(INPUT|SELECT|TEXTAREA)$/.test(target.tagName)) return true;
  return target.closest('[role="dialog"]') !== null;
}

export function useFullWidth(): FullWidthState {
  const { open: sidebarOpen, setOpen: setSidebarOpen } = useSidebarState();
  const { railOpen, openRail, closeRail } = useChat();
  const [fullWidth, setFullWidth] = useState(false);
  const before = useRef<PanelSnapshot | null>(null);

  const toggleFullWidth = useCallback(() => {
    const snapshot = before.current;
    if (snapshot) {
      before.current = null;
      setFullWidth(false);
      if (snapshot.sidebar) setSidebarOpen(true);
      if (snapshot.rail) openRail();
      return;
    }
    before.current = { sidebar: sidebarOpen, rail: railOpen };
    setFullWidth(true);
    setSidebarOpen(false);
    closeRail();
  }, [sidebarOpen, railOpen, setSidebarOpen, openRail, closeRail]);

  // A panel reopened by hand ends the mode; there is nothing left to restore.
  useEffect(() => {
    if (fullWidth && (sidebarOpen || railOpen)) {
      before.current = null;
      setFullWidth(false);
    }
  }, [fullWidth, sidebarOpen, railOpen]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'f' || e.repeat || e.defaultPrevented) return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      if (typingIn(e.target)) return;
      e.preventDefault();
      toggleFullWidth();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleFullWidth]);

  return { fullWidth, toggleFullWidth };
}
